import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';

interface FilterOptionRow {
  field_key: string;
  label: string;
  input_type: string;
  options: string[];
  depends_on?: string;
  grouped_options?: Record<string, string[]>;
  is_enabled: boolean;
  is_advanced: boolean;
  display_order: number;
}

const DEFAULT_NEW_ROW: FilterOptionRow = {
  field_key: '',
  label: '',
  input_type: 'select',
  options: [],
  depends_on: '',
  grouped_options: {},
  is_enabled: true,
  is_advanced: false,
  display_order: 999,
};

const AdminFilterOptionsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rows, setRows] = useState<FilterOptionRow[]>([]);

  const normalizeKey = (rawValue: string) => rawValue.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^_+|_+$/g, '');

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/profiles/admin/filter-config');
      const items: FilterOptionRow[] = (response.data.items || []).map((item: any, index: number) => ({
        ...item,
        input_type: item.input_type || 'select',
        options: item.options || [],
        depends_on: item.depends_on || '',
        grouped_options: item.grouped_options || {},
        is_enabled: Boolean(item.is_enabled),
        is_advanced: Boolean(item.is_advanced),
        display_order: item.display_order ?? (index + 1) * 10,
      }));
      setRows(items);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load filter options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchConfig();
  }, []);

  const updateRow = (index: number, patch: Partial<FilterOptionRow>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addField = () => {
    setRows((prev) => [...prev, { ...DEFAULT_NEW_ROW, display_order: prev.length * 10 + 10 }]);
  };

  const removeField = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const saveConfig = async () => {
    const invalid = rows.find((row) => !normalizeKey(row.field_key || row.label || '') || !row.label.trim());
    if (invalid) {
      toast.error('Each filter must have a key and label');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        items: rows.map((row, index) => ({
          ...row,
          field_key: normalizeKey(row.field_key || row.label),
          label: row.label.trim(),
          display_order: (index + 1) * 10,
          options: row.options.map((option) => option.trim()).filter(Boolean),
          depends_on: row.depends_on || '',
          grouped_options: row.grouped_options || {},
          is_enabled: Boolean(row.is_enabled),
          is_advanced: Boolean(row.is_advanced),
        })),
      };
      const response = await api.put('/profiles/admin/filter-config', payload);
      setRows(response.data.items || []);
      toast.success('Filter options saved');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save filter options');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout currentPage="filter-options">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Filter Options
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage browse filters separately from profile fields. Changes here are reflected on user browse immediately.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Keep commonly used filters as Basic. Move detailed filters to Advanced so user search stays clean.
      </Alert>

      <Box sx={{ mb: 1.5 }}>
        <FormControlLabel
          control={<Switch checked={showAdvanced} onChange={(e) => setShowAdvanced(e.target.checked)} />}
          label="Advanced mode (show technical keys and dependency mapping)"
        />
      </Box>

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {rows.map((row, index) => (
            <Card key={`${row.field_key}-${index}`}>
              <CardContent>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: showAdvanced ? '1fr 1fr 160px 140px 120px 120px auto' : '1.3fr 160px 140px 120px 120px auto' }, gap: 1.25 }}>
                  {showAdvanced && (
                    <TextField
                      size="small"
                      label="Filter Key"
                      value={row.field_key}
                      onChange={(e) => updateRow(index, { field_key: normalizeKey(e.target.value) })}
                    />
                  )}

                  <TextField
                    size="small"
                    label="Label"
                    value={row.label}
                    onChange={(e) => {
                      const nextLabel = e.target.value;
                      const patch: Partial<FilterOptionRow> = { label: nextLabel };
                      if (!showAdvanced && !row.field_key.trim()) {
                        patch.field_key = normalizeKey(nextLabel);
                      }
                      updateRow(index, patch);
                    }}
                  />

                  <TextField
                    size="small"
                    select
                    label="Input"
                    value={row.input_type}
                    onChange={(e) => updateRow(index, { input_type: e.target.value })}
                  >
                    <MenuItem value="select">select</MenuItem>
                    <MenuItem value="text">text</MenuItem>
                  </TextField>

                  <TextField
                    size="small"
                    select
                    label="Enabled"
                    value={row.is_enabled ? 'YES' : 'NO'}
                    onChange={(e) => updateRow(index, { is_enabled: e.target.value === 'YES' })}
                  >
                    <MenuItem value="YES">Yes</MenuItem>
                    <MenuItem value="NO">No</MenuItem>
                  </TextField>

                  <TextField
                    size="small"
                    select
                    label="Group"
                    value={row.is_advanced ? 'ADVANCED' : 'BASIC'}
                    onChange={(e) => updateRow(index, { is_advanced: e.target.value === 'ADVANCED' })}
                  >
                    <MenuItem value="BASIC">Basic</MenuItem>
                    <MenuItem value="ADVANCED">Advanced</MenuItem>
                  </TextField>

                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    {!showAdvanced && row.field_key && <Chip size="small" variant="outlined" label={`Key: ${row.field_key}`} />}
                    <Button color="error" variant="outlined" onClick={() => removeField(index)}>
                      Remove
                    </Button>
                  </Stack>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: showAdvanced ? '1fr 220px' : '1fr' }, gap: 1.25, mt: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Options (comma separated)"
                      helperText="Type options separated by commas. Delete text and commas to remove options."
                      value={(row.options || []).join(', ')}
                      onChange={(e) => updateRow(index, { options: e.target.value.split(',').map((value) => value.trim()) })}
                    />
                    <Button
                      aria-label="Clear options"
                      title="Clear all options"
                      variant="outlined"
                      color="error"
                      onClick={() => updateRow(index, { options: [] })}
                      sx={{ minWidth: 42, height: 40, px: 1 }}
                    >
                      <ClearIcon fontSize="small" />
                    </Button>
                  </Box>

                  {showAdvanced && (
                    <TextField
                      size="small"
                      select
                      fullWidth
                      label="Depends On"
                      value={row.depends_on || ''}
                      onChange={(e) => updateRow(index, { depends_on: e.target.value })}
                    >
                      <MenuItem value="">None</MenuItem>
                      {rows
                        .filter((candidate, candidateIndex) => candidateIndex !== index && candidate.field_key)
                        .map((candidate) => (
                          <MenuItem key={`depends-${candidate.field_key}`} value={candidate.field_key}>
                            {candidate.label} ({candidate.field_key})
                          </MenuItem>
                        ))}
                    </TextField>
                  )}

                  {showAdvanced && (
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={row.depends_on ? 4 : 3}
                      sx={{ gridColumn: '1 / -1' }}
                      label={row.depends_on ? 'Dependent options by parent value' : 'Optional grouped options'}
                      helperText={row.depends_on ? 'One rule per line. Example: Hindu: Brahmin, Rajput' : 'Optional grouped mapping.'}
                      value={Object.entries(row.grouped_options || {})
                        .map(([key, values]) => `${key}: ${(values || []).join(', ')}`)
                        .join('\n')}
                      onChange={(e) => {
                        const grouped_options: Record<string, string[]> = {};
                        e.target.value.split('\n').forEach((line) => {
                          const [parent, childValues] = line.split(':');
                          const parentKey = String(parent || '').trim();
                          if (!parentKey) return;
                          grouped_options[parentKey] = String(childValues || '')
                            .split(',')
                            .map((value) => value.trim())
                            .filter(Boolean);
                        });
                        updateRow(index, { grouped_options });
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addField}>
              Add Filter
            </Button>
            <Button variant="contained" onClick={saveConfig} disabled={saving}>
              {saving ? 'Saving...' : 'Save Filter Options'}
            </Button>
          </Stack>
        </Stack>
      )}
    </AdminLayout>
  );
};

export default AdminFilterOptionsPage;
